import { getRouteApi, Link } from "@tanstack/react-router";
import { Button, buttonVariants } from "@workspace/web-ui/components/button";
import { Image } from "@workspace/web-ui/components/image";
import { Bell, Calendar, Globe, Info, Mail, User } from "lucide-react";
import { toast } from "sonner";
import { useUpdateAppointmentMutation } from "../../quick-actions/api/mutations/appointment";
import { cn } from "@workspace/web-ui/lib/utils";
import { quickActionsKeys } from "../../quick-actions/api/queries";
import { getQueryClient } from "~/providers/query.provider";
import { TAG_STYLES } from "@workspace/web-ui/lib/encounter";
import { useSendActionNeededMutation } from "../../patient-logs/api/mutations";
import { AppointmentStatusCard } from "../../quick-actions/components/cards/appointment-status";
import { encountersKeys } from "../../encounters/api/queries";
import { RecommendedTask } from "../../encounters/components/recommended-task";

const COLORS = {
    TEXT_PRIMARY: '#2D2F35',
    TEXT_SECONDARY: '#667085',
} as const;

export interface AppointmentEvent {
  type: 'appointment'
  appointment_id?: string
  title?: string
  subtitle?: string
  description?: string
  location?: string
  appointment_datetime?: string
  start?: Date
  time_interval_minutes?: number
  clientName?: string
  email?: string
  doctorName?: string
  status?: 'scheduled' | 'completed' | 'cancelled'
  isFollowUp?: boolean
  zoom_metadata?: {
    uuid?: string
    id?: number
    host_id?: string
    topic?: string
    start_time?: string
    duration?: number
    timezone?: string
    created_at?: string
    start_url?: string
    join_url?: string
  }
  patient?: {
    user_id?: string
    name?: string
    medical_plan?: {
      program?: string
      medication?: {
        name?: string
        cost?: number
        substitute_reason?: string | null
        substitute?: boolean
      }
      vitamin?: {
        name?: string
      }
      consultation?: {
        name?: string
        cost?: number
      }
      session_id?: string | null
      success_url?: string
      cancel_url?: string
      cost?: number
      plan_id?: string
      user_id?: string
      payment_status?: string
      prescribed?: boolean
      created_datetime?: string
      created_at?: number
    }
  }
  session?: {
    user_id?: string
    session_id?: string
    session_type?: string
    entry_type?: string
    start_datetime?: string
    status?: string
    payment_status?: string
    treatment_completed?: boolean
    created_at?: number
    thread_id?: string
    agent_id?: string
    alfie_session?: string
    reported_symptom?: string | null
    symptoms?: unknown | null
    final_questions?: Record<string, string | null> | null
    intake_questions?: Record<string, string | null>
    red_flag_questions?: Record<string, string | null>
    diagnosis?: Array<{
      disease_name?: string
      disease_id?: string
      image_prompt?: string
      overview?: string
      symptoms?: string[]
      red_flags?: string[]
      diagnosis?: string[]
      causes?: string[]
      prevention?: string[]
      treatment_page?: {
        red_flag_questions?: string[]
        additional_question?: string | null
        medications?: Array<{
          category?: string
          medication?: string
          quantity?: string
          instructions?: string[]
          is_primary?: boolean
          is_secondary?: boolean
          is_extra?: boolean
        }>
      }
      general_questions?: string[]
      affected_area?: string[]
      image_urls?: string[]
    }>
  }
  encounter?: {
    type?: string
    user_id?: string
    appointment_id?: string
    program?: string
    encounter_datetime?: string
    recommended_tasks?: any[]
    time_interval_minutes?: number
    created_at?: number
    session_id?: string
    is_first?: boolean
    encounter_id?: string
    notes?: string | null
  }
}

interface InfoRowProps {
    icon: typeof User | typeof Mail | typeof Calendar | typeof Globe | typeof Bell;
    value: string;
    testId: string;
}

const InfoRow = ({ icon: Icon, value, testId }: InfoRowProps) => (
    <div className="flex gap-3 items-center" data-testid={testId}>
        <Icon className="w-4 h-4 text-[#7F8493]" />
        <span className="text-sm font-normal text-[#202020CC]">{value}</span>
    </div>
);

interface AppointmentDetailsFromDataProps {
    appointment: AppointmentEvent;
}

// Helper functions
const formatDate = (dateString?: string, format: "datetime" | "date" = "datetime"): string => {
    if (!dateString) return "N/A";
    
    try {
        const date = new Date(dateString);
        if (format === "datetime") {
            return date.toLocaleString();
        }
        return date.toLocaleDateString();
    } catch {
        return "N/A";
    }
};

const isHappeningNow = (dateString?: string): string => {
    if (!dateString) return "Unknown";
    
    try {
        const appointmentTime = new Date(dateString);
        const now = new Date();
        const timeDiff = appointmentTime.getTime() - now.getTime();
        const hoursDiff = timeDiff / (1000 * 60 * 60);
        
        if (Math.abs(hoursDiff) < 1) {
            return "Happening now";
        } else if (appointmentTime > now) {
            return "Upcoming";
        } else {
            return "Past";
        }
    } catch {
        return "Unknown";
    }
};

const getAppointmentStatus = (dateString?: string, status?: string): string => {
    if (status === 'cancelled') return 'cancelled';
    if (status === 'completed') return 'completed';
    
    const timeStatus = isHappeningNow(dateString);
    if (timeStatus === "Happening now") return "happening";
    if (timeStatus === "Past") return "past";
    return "upcoming";
};

// Skeleton loader for appointment details
// function AppointmentDetailsSkeleton() {
//     return (
//         <div className="flex flex-col w-full h-full bg-white rounded-[24px] border border-[#0404041A] overflow-auto gap-4 sm:gap-5 p-4 sm:py-4 sm:px-4 animate-pulse">
//             <div className="h-6 w-1/2 bg-gray-200 rounded mb-2" />
//             <div className="flex flex-col sm:flex-row gap-3 sm:gap-2 items-start sm:items-center justify-between">
//                 <div className="flex flex-col gap-2 w-full sm:w-auto">
//                     <div className="h-8 w-2/3 bg-gray-200 rounded mb-1" />
//                     <div className="flex gap-2">
//                         <div className="h-4 w-20 bg-gray-200 rounded" />
//                         <div className="h-4 w-12 bg-gray-200 rounded" />
//                         <div className="h-4 w-10 bg-gray-200 rounded" />
//                     </div>
//                 </div>
//                 <div className="h-10 w-32 bg-gray-200 rounded" />
//             </div>
//             <div className="flex flex-col gap-2 mt-2 sm:mt-5">
//                 {[...Array(5)].map((_, i) => (
//                     <div key={i} className="h-4 w-full bg-gray-200 rounded" />
//                 ))}
//             </div>
//             <div className="h-4 w-1/3 bg-gray-200 rounded mt-2" />
//             <div className="h-12 w-full bg-gray-200 rounded mt-2" />
//             <div className="h-10 w-1/2 bg-gray-200 rounded mt-2" />
//         </div>
//     );
// }

// Main component
export const AppointmentDetailsFromData = ({ appointment }: AppointmentDetailsFromDataProps) => {
    const routeApi = getRouteApi("/(dashboard)/dashboard/$program");
    const { program } = routeApi.useParams();
    const queryClient = getQueryClient();
    const UpdateAppointmentMutation = useUpdateAppointmentMutation({
        id: appointment.appointment_id || ""
    });
    const { mutate: sendActionNeeded, isPending: sendActionPending } = useSendActionNeededMutation();
    
    // Safely get program from multiple sources
    const appointmentProgram = appointment.encounter?.program || appointment.patient?.medical_plan?.program || program;
    
    // Create encounter-like object for compatibility with existing components
    const encounterCompat = {
        id: appointment.appointment_id || "",
        encounterId: appointment.encounter?.encounter_id || "",
        status: getAppointmentStatus(appointment.appointment_datetime, appointment.status),
        patient: {
            id: appointment.patient?.user_id || "",
            name: appointment.patient?.name || appointment.clientName || "Unknown Patient",
            email: appointment.email || "",
            medical_plan: appointment.patient?.medical_plan || null,
        },
        date: formatDate(appointment.appointment_datetime),
        tag: appointment.subtitle || "Appointment",
    };

    const infoRows = [
        {
            icon: User,
            value: appointment.patient?.name || appointment.clientName || "N/A",
            testId: 'patient-name'
        },
        {
            icon: Calendar,
            value: formatDate(
                appointment.start instanceof Date
                    ? appointment.start.toISOString()
                    : appointment.start,
                "datetime"
            ),
            testId: 'appointment-datetime'
        },
        {
            icon: Globe,
            value: appointment.zoom_metadata?.timezone || appointment.location || "N/A",
            testId: 'appointment-location'
        },
        // {
        //     icon: Mail,
        //     value: appointment.email || "N/A",
        //     testId: 'patient-email'
        // },
        {
            icon: Bell,
            value: isHappeningNow(
                appointment.start instanceof Date
                    ? appointment.start.toISOString()
                    : appointment.start
            ),
            testId: 'appointment-status'
        }
    ]

    const handleMarkAppointmentStatus = async (status: 'attended' | 'cancelled') => {
        const meetingStatus = status === "cancelled" ? "not-held" : "held";
        try {
            await UpdateAppointmentMutation.mutateAsync({
                status,
            }, {
                onSuccess(data) {
                    if (data.status === "success") {
                        toast.success(`Appointment marked as ${meetingStatus}`, {
                            style: {
                                background: "#6EE7B7",
                                color: "#FFFFFF",
                                borderColor: "#6EE7B7",
                                "& svg": {
                                    color: "#FFFFFF",
                                },
                            } as React.CSSProperties,
                        });
                    }
                    queryClient.invalidateQueries({ 
                        queryKey: [
                            quickActionsKeys.appointmentById(appointment.appointment_id || ""), 
                            encountersKeys.all
                        ] 
                    });
                },
                onError() {
                    toast.error(`An error occurred while updating appointment status as ${meetingStatus}`);
                },
            });
        } catch (error) {
            console.log("error updating appointment status", error);
        }
    };

    const appointmentStatus = getAppointmentStatus(appointment.appointment_datetime, appointment.status);
    const isHappening = appointmentStatus === "happening";
    const isPast = appointmentStatus === "past";
    const isVerified = appointment.patient?.medical_plan?.payment_status === "paid";

    // Show skeleton if appointment is loading (you can add loading prop if needed)
    // if (isLoading) {
    //     return <AppointmentDetailsSkeleton />;
    // }

    return (
        <>
            {appointmentProgram === "weight_loss" && (
                <div className="flex flex-col sm:w-full h-full bg-[#FFFFFF] rounded-[24px] border border-[#0404041A] overflow-auto gap-4 sm:gap-5 p-4 sm:py-[16px] sm:px-[32px]">
                    <div
                        className={cn(
                            "flex flex-row gap-[2px] items-center rounded-[888px] px-[6px] py-[4px] w-fit",
                            TAG_STYLES[appointment.subtitle?.toLowerCase() as keyof typeof TAG_STYLES] || TAG_STYLES.urgent
                        )}
                        data-testid="consultation-status"
                    >
                        <Calendar className="size-4" />
                        <span className="text-xs font-medium">{appointment.subtitle || "Appointment"}</span>
                    </div>
                    
                    {/* Appointment description/title */}
                    <h2 className="text-lg sm:text-xl font-medium">
                        {appointment.title || appointment.description || "Weight Loss Consultation"}
                    </h2>
                    
                    {/* Patient info section */}
                    <div className="flex flex-col sm:flex-row gap-3 sm:gap-2 items-start sm:items-center justify-between w-full">
                        <div className="flex flex-col gap-2 w-full sm:w-auto">
                            <div className="flex items-center gap-2">
                                <h2 className="text-2xl sm:text-3xl font-bold truncate max-w-full">
                                    {appointment.patient?.name || appointment.clientName || "Unknown Patient"}
                                </h2>
                                {isVerified && (
                                    <div className="">
                                        <Image src="/icons/check.svg" alt="verified" layout="constrained" className="w-4 h-4 sm:w-5 sm:h-5" width={96} height={96} />
                                    </div>
                                )}
                            </div>
                            <div className="flex flex-wrap items-center gap-y-1 gap-x-2">
                                <span className="text-sm" style={{ color: COLORS.TEXT_PRIMARY }}>
                                    Weight Loss Program
                                </span>
                                <span className="hidden sm:inline mx-1" style={{ color: COLORS.TEXT_SECONDARY }}>•</span>
                                <span className="text-sm" style={{ color: COLORS.TEXT_PRIMARY }}>
                                    N/A years
                                </span>
                                <span className="hidden sm:inline mx-1" style={{ color: COLORS.TEXT_SECONDARY }}>•</span>
                                <span className="text-sm" style={{ color: COLORS.TEXT_PRIMARY }}>
                                    N/A
                                </span>
                            </div>
                        </div>
                    </div>
                    
                    {/* Patient information rows */}
                    <div className="flex flex-col gap-3 mt-2 sm:mt-5 w-full">
                        {infoRows.map(({ icon, value, testId }) => (
                            <InfoRow
                                key={testId}
                                icon={icon}
                                value={value}
                                testId={testId}
                            />
                        ))}
                    </div>
                    
                    {/* Creation date */}
                    <h2 className="text-xs font-normal text-[#202020CC]">
                        Created {formatDate(appointment.zoom_metadata?.created_at)}
                    </h2>
                    
                    <div className="flex flex-col md:flex-row items-center gap-3 p-4 w-full bg-[#EBF0FB] rounded-lg">
                        <Info className="size-6 text-[#4272DD]" />
                        {isHappening ? (
                            <p className="text-sm font-normal text-[#202020CC]">This Appointment is live.</p>
                        ) : (
                            <p className="text-xs font-normal text-[#202020CC] whitespace-normal">
                                This appointment is scheduled for {formatDate(appointment.appointment_datetime)}. Please ensure you're available 5 minutes before the start time.
                            </p>
                        )}
                        {isHappening && (
                            <Link
                                className={cn(buttonVariants({ variant: "outline" }), "bg-transparent w-[135px] text-[#4272DD] justify-self-end rounded-[8px] border-none py-3 px-4 hover:bg-[#4272DD] hover:text-white")}
                                to="/dashboard/$program/patients/patient/$id"
                                params={{
                                    id: appointment.patient?.user_id || "",
                                    program
                                }}
                                search={{
                                    name: (appointment.patient?.name || appointment.clientName || "").replace(/\s+/g, ""),
                                    sessionId: appointment?.session?.session_id || ""
                                }}
                            >
                                View Patient Log
                            </Link>
                        )}
                        <a 
                            href={appointment.zoom_metadata?.join_url || "#"}
                            target="_blank" 
                            rel="noopener noreferrer"
                            className={cn(
                                buttonVariants({ variant: "default" }), 
                                "bg-[#4272DD] rounded-lg w-full sm:w-auto", 
                                !isHappening || !appointment.zoom_metadata?.join_url ? "pointer-events-none cursor-not-allowed opacity-60 no-underline" : ""
                            )}
                        >
                            Join meeting
                        </a>
                    </div>
                    
                    {/* Appointment Status Card */}
                    <div className="flex gap-4 w-full flex-wrap">
                        {(isHappening || isPast || appointment.status === "cancelled") && (
                            <div className="flex flex-col w-full">
                                <AppointmentStatusCard
                                    status={appointmentStatus}
                                    encounter={encounterCompat}
                                    onStatusChange={handleMarkAppointmentStatus}
                                />
                            </div>
                        )}
                    </div>
                    
                    {/* Recommended Task or Verification Reminder */}
                    {["happening", "past", "completed", "attended"].includes(appointmentStatus) && (
                        <>
                            {!isVerified && (
                                <div className="flex items-center bg-[#F0F5FF] rounded-lg px-4 py-2 justify-between w-full">
                                    <p className="text-sm text-[#1A202C]">
                                        Patient must be verified
                                    </p>
                                    <Button
                                        className="button-primary !bg-[#4272DD] h-[44px] !text-white flex gap-2 items-center !shadow-none w-fit !border-none"
                                        onClick={async () => {
                                            try {
                                                await sendActionNeeded({ 
                                                    patient_id: appointment.patient?.user_id || "" 
                                                });
                                                toast.success("Reminder sent to patient to complete verification.");
                                            } catch (error) {
                                                toast.error("Failed to send reminder.");
                                            }
                                        }}
                                        disabled={sendActionPending || !appointment.patient?.user_id}
                                    >
                                        {sendActionPending && (
                                            <svg className="animate-spin h-4 w-4 mr-2 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                                                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                                                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v4a4 4 0 00-4 4H4z"></path>
                                            </svg>
                                        )}
                                        Send a Reminder
                                    </Button>
                                </div>
                            )}
                            {appointment.encounter?.encounter_id && (
                                <RecommendedTask 
                                    encounterId={appointment.encounter.encounter_id} 
                                    patientId={appointment.patient?.user_id || ""} 
                                    showAddButton={isVerified}
                                />
                            )}
                        </>
                    )}
                </div>
            )}
            
            {appointmentProgram === "urgent_care" && (
                <div className="flex flex-col sm:w-full h-full bg-[#FFFFFF] rounded-[24px] overflow-auto gap-4 sm:gap-5 sm:py-[16px]">
                    <div
                        className={cn(
                            "flex flex-row gap-[2px] items-center rounded-[888px] px-[6px] py-[4px] w-fit",
                            TAG_STYLES[appointment.subtitle?.toLowerCase() as keyof typeof TAG_STYLES] || TAG_STYLES.urgent
                        )}
                        data-testid="consultation-status"
                    >
                        <Calendar className="size-4" />
                        <span className="text-xs font-medium">{appointment.subtitle || "Urgent"}</span>
                    </div>
                    
                    {/* Patient info section */}
                    <div className="flex flex-col sm:flex-row gap-3 sm:gap-2 items-start sm:items-center justify-between w-full">
                        <div className="flex flex-col gap-2 w-full">
                            <div className="flex items-center gap-2">
                                <h2 className="text-2xl sm:text-3xl font-bold truncate max-w-full">
                                    {appointment.patient?.name || appointment.clientName || "Unknown Patient"}
                                </h2>
                                {isVerified && (
                                    <div className="">
                                        <Image src="/icons/check.svg" alt="verified" layout="constrained" className="w-4 h-4 sm:w-5 sm:h-5" width={96} height={96} />
                                    </div>
                                )}
                            </div>
                            <div className="flex flex-col w-full text-left pb-1">
                                <div className="flex justify-between w-[80%]">
                                    <div className="text-[#464B55] text-sm">
                                        Submitted:
                                        <p className="text-[#1A202C] text-sm">
                                            {formatDate(appointment.zoom_metadata?.created_at)}
                                        </p>
                                    </div>
                                    <div className="text-[#464B55] text-sm">
                                        Program:
                                        <p className="text-[#1A202C] text-sm">Urgent Care</p>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                    
                    {/* Patient information rows */}
                    <div className="flex flex-col gap-3 sm:mt-3 w-full border rounded-md p-3">
                        {infoRows.map(({ icon, value, testId }) => (
                            <InfoRow
                                key={testId}
                                icon={icon}
                                value={value}
                                testId={testId}
                            />
                        ))}
                    </div>
                    
                    <div className="flex flex-col md:flex-row items-center gap-3 p-4 w-full bg-[#EBF0FB] rounded-lg">
                        <Info className="size-6 text-[#4272DD]" />
                        {isHappening ? (
                            <p className="text-sm font-normal text-[#202020CC]">This Appointment is live.</p>
                        ) : (
                            <p className="text-xs font-normal text-[#202020CC] whitespace-normal">
                                This appointment is scheduled for {formatDate(appointment.start?.toString())}. Please ensure you're available 5 minutes before the start time.
                            </p>
                        )}
                        {isHappening && (
                            <Link
                                className={cn(buttonVariants({ variant: "outline" }), "bg-transparent w-[135px] text-[#4272DD] justify-self-end rounded-[8px] border-none py-3 px-4 hover:bg-[#4272DD] hover:text-white")}
                                to="/dashboard/$program/patients/patient/$id"
                                params={{
                                    id: appointment.patient?.user_id || "",
                                    program
                                }}
                                search={{
                                    name: (appointment.patient?.name || appointment.clientName || "").replace(/\s+/g, ""),
                                    sessionId: appointment?.session?.session_id || ""
                                }}
                            >
                                View Patient Log
                            </Link>
                        )}
                        <a 
                            href={appointment.zoom_metadata?.join_url || "#"}
                            target="_blank" 
                            rel="noopener noreferrer"
                            className={cn(
                                buttonVariants({ variant: "default" }), 
                                "bg-[#4272DD] rounded-lg w-full sm:w-auto", 
                                !isHappening || !appointment.zoom_metadata?.join_url ? "pointer-events-none cursor-not-allowed opacity-60 no-underline" : ""
                            )}
                        >
                            Join meeting
                        </a>
                    </div>
                    
                    {/* Session information for urgent care */}
                    {appointment.session && appointment.session.diagnosis && appointment.session.diagnosis.length > 0 && (
                        <div className="w-full flex flex-col gap-4 bg-[#EBF0FB] p-4 rounded-[8px] mt-3">
                            <div className="flex flex-col space-y-1 flex-1">
                                <p className="text-[#1A202C] font-semibold">Reported Symptoms</p>
                                <p className="text-[#464B55] text-[14px]">
                                    {appointment.session.reported_symptom || "No symptoms reported"}
                                </p>
                            </div>
                            <hr />
                            <div className="flex flex-col space-y-1 flex-1">
                                <p className="text-[#1A202C] font-semibold">Alfie's Diagnosis</p>
                                {appointment.session.diagnosis.map((diag, index) => (
                                    <div key={index}>
                                        <p className="text-[#464B55] text-[14px]">
                                            {diag.disease_name} - {diag.overview}
                                        </p>
                                        {diag.treatment_page?.medications?.length ? (
                                            <p className="text-[#464B55] text-[14px]">
                                                Recommended Treatment: {diag.treatment_page.medications?.[0]?.medication || "N/A"}
                                            </p>
                                        ) : null}
                                    </div>
                                ))}
                            </div>
                            <hr />
                            <div className="flex flex-col space-y-1 flex-1">
                                <p className="text-[#1A202C] font-semibold">Alfie's Recommended treatment</p>
                                {appointment.session.diagnosis.map((diag, index) => (
                                    <div key={index}>
                                        <p className="text-[#464B55] text-[14px]">
                                            {diag.disease_name} - {diag.overview}
                                        </p>
                                        {diag.treatment_page?.medications?.map((med, medIndex) => (
                                            <p key={medIndex} className="text-[#464B55] text-[14px]">
                                                {med.medication} - {med.instructions?.join(", ") || "No instructions"}
                                            </p>
                                        ))}
                                    </div>
                                ))}
                            </div>
                            <Link
                                className={cn(buttonVariants({ variant: "outline" }), "w-full h-[44px] rounded-[10px] bg-[#F8FAFF] text-[#4272DD] border border-[#4272DD] py-3 px-4 hover:bg-[#4272DD] hover:text-white")}
                                to="/dashboard/$program/patients/patient/$id"
                                params={{
                                    program,
                                    id: appointment.session?.user_id || ""
                                }}
                                search={{
                                    name: (appointment.patient?.name || appointment.clientName || "").replace(/\s+/g, ""),
                                    sessionId: appointment?.session?.session_id || ""
                                }}
                            >
                                Go to encounter session
                            </Link>
                        </div>
                    )}
                    
                    {/* Appointment Status Card */}
                    <div className="flex gap-4 w-full flex-wrap">
                        <div className="flex flex-col w-full">
                            <AppointmentStatusCard
                                status={appointmentStatus}
                                encounter={encounterCompat}
                                onStatusChange={handleMarkAppointmentStatus}
                            />
                        </div>
                    </div>
                </div>
            )}
        </>
    );
};